import {Button, CircularProgress, Grid, IconButton, LinearProgress, Paper} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import CatalogTab from "../../catalog/components/catalog_tab";
import React, {Fragment, useEffect} from "react";
import {
    attributeFields, attributeSetFields,
    categoryFields, importAttributes, importAttributeSets, importCategories, unescape
} from "../../../../../utils/utils";
import axios from "axios";
import Config from "../../../../../config";
import DropZone from "../../../../files/dropzone";
import {setDefaultMappings} from "../../../../inventory/v1/catalog_import";
import {Notifier} from "../../../../documents/document_components";
import EnhancedImportTable from "./components/EnhancedImportTable";
import {SectionTitle} from "../../export/advanced_export";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import {loadProfiles} from "../../../../files/import_export_profiles";
import {Helpers, StorageHelper} from "../../../../../helpers";
import EnhancedProfileOrig from "../../export/components/EnhancedProfile";
import {pageContentGap, Header} from "../../../../../theme";
import cloneDeep from 'lodash/cloneDeep';
import GenericDialog from "../../../../../components/Dialogs/GenericDialog";
import type {SettingsImportProfile} from "../../../types";
const EnhancedProfile = EnhancedProfileOrig as any;

// region TypeScript Interfaces
interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: unknown;
}

interface RouteMatch {
    params: {
        type?: string;
    };
}

interface CatalogManagerAdvancedImportProps {
    match: RouteMatch;
    history: any;
    location: any;
    setTitle?: (title: string, hideBackButton?: boolean) => void;
}

interface ParsedField {
    field: string;
    alias: string;
    content?: string;
    scientificNotation?: string;
}
// endregion

const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
const storageHelper = new StorageHelper();
const importTypes = ["categories", "attributes", "attributeSets"];
const blankProfile: SettingsImportProfile = {
    DocumentType: "ProductOnboardingCenterCatalog",
    Name: '',
    Delimiter: ",",
    FileType: "csv",
    Partner: "",
    Filter: undefined,
    Fields: [],
};
export default function CatalogManagerAdvancedImport(props: CatalogManagerAdvancedImportProps) {
    if (props.match.params.type === undefined) props.history.replace("/productonboardingcenter/import/categories");
    if (!importTypes.includes(props.match.params.type as string)) props.history.replace("/productonboardingcenter/import/categories");
    const {setTitle} = props;
    // const storagePartnerId = "LBCatalogExportSelectedPartnerId";
    const storageProfileName = "LBCatalogImportProfileName";
    //region URL Params
    const type = props.match.params.type as string; // | categories | attributes | attributeSets
    let profileType = "ImportCatalogCategories";
    if (type === "categories") profileType = "ImportCatalogCategories";
    else if (type === "attributes") profileType = "ImportCatalogAttributes";
    else if (type === "attributeSets") profileType = "ImportCatalogAttributeSets";
    const [currentImportType, setCurrentImportType] = React.useState<string>(type || "categories");
    //endregion
    const [updated, setUpdated] = React.useState<boolean>(false);
    const [updating, setUpdating] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    // setError pattern matches advanced_supplier_import: literal `false` initial,
    // setter calls pass {open, errorHeader, errorMessage} ErrorState objects.
    const [error, setError] = React.useState<false | ErrorState>(false);
    const [profile, setProfile] = React.useState<SettingsImportProfile>({...blankProfile});
    const [availableProfiles, setAvailableProfiles] = React.useState<SettingsImportProfile[]>([]);
    //region Import State
    const [file, setFile] = React.useState<File | null>(null);
    const [parsedFields, setParsedFields] = React.useState<ParsedField[]>([]);
    const [validationWarnings, setValidationWarnings] = React.useState<string[]>([]);
    //endregion
    //region Functions
    const importItems = async () => {
        let formattedProfile: any = cloneDeep(profile);
        if (formattedProfile.Fields.length > 0) {
            formattedProfile.Columns = formattedProfile.Fields;
            formattedProfile.Columns = formattedProfile.Columns.map((column: any) => {
                return {
                    SourceField: column.field,
                    TargetField: column.alias,
                }
            });
        }
        let runStatus: any = {};
        setUpdated(false);
        setUpdating(true);
        switch (type) {
            case "categories":
                runStatus = await (importCategories as any)(formattedProfile);
                break;
            case "attributes":
                runStatus = await (importAttributes as any)(formattedProfile);
                break;
            case "attributeSets":
                runStatus = await (importAttributeSets as any)(formattedProfile);
                break;
            default:
                break;
        }
        if (runStatus.errorMessage)
            setError(runStatus);
        else {
            let formData = new FormData();
            formData.append("file", file as any);
            try {
                let uploadedFiles: any = (await axios.post(runStatus, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })).data;
                let importErrors: string[] = [];
                const errorType = type === "categories" ? "Category" : type === "attributes" ? "Attribute" : "Attribute Set";
                const errorMessageIndex = type === "categories" ? "Name" : type === "attributes" ? "FriendlyName" : "Name";
                uploadedFiles.Records.forEach((r: any) => {
                    if (r.Code !== "Success")
                        importErrors.push(`${importErrors.length + 1} - ${errorType} "${r.Body[errorMessageIndex]}" failed with error: ${r.Message}`);
                });
                if (importErrors.length > 0)
                    setError({
                        open: true,
                        errorHeader: "Import completed with errors",
                        errorMessage: importErrors.join("\n")
                    });
                else setUpdated(true)
            } catch (error: any) {
                let errorMessage = error.message;
                if (error.response != null) errorMessage = error.response.data.Message;
                setError({
                    open: true,
                    errorHeader: "Error uploading import!",
                    errorMessage: errorMessage
                });
            }
        }
        setUpdating(false);
    }
    const updateImportType = (type: string) => {
        setCurrentImportType(type);
        props.history.replace(`/productonboardingcenter/import/${type}`);
    }
    const parseFile = async (file: File) => {
        let fileType = 'csv';
        if (file.name && file.name.endsWith(".xlsx")) fileType = 'xlsx';
        try {
            let formData = new FormData();
            formData.append("file", file);
            let parsed: any = (await axios.post(Config.api + `/odata/company/Functions.ParseFlatFile?docType=ProductOnboardingCenterCatalog&fileType=${fileType}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })).data;
            let buildingProfile: any = {
                Fields: parsed.Columns.map((r: any) => ({field: r.Input, alias: r.Output, content: r.SampleContent}))
            }
            let options: string[] = [];
            if (type === "categories") options = categoryFields as any;
            else if (type === "attributes") options = attributeFields as any;
            else if (type === "attributeSets") options = attributeSetFields as any;
            (setDefaultMappings as any)(buildingProfile.Fields, options.map((option: string) => ({Name: option, FriendlyName: option})));
            let parsedFields: ParsedField[] = parsed.Columns.map((r: any) => ({
                field: r.Input,
                alias: r.Output,
                content: r.SampleContent,
                scientificNotation: r.ScientificNotation
            }));
            let parsedWarnings: string[] = parsedFields.filter((r: any) => !(!r.scientificNotation)).map((r: any) => {
                return (
                    `Column ${r.field} appears to contain scientific notation. Sample value: ${r.scientificNotation}`
                );
            });
            setProfile(buildingProfile);
            setParsedFields(parsedFields);
            setValidationWarnings(parsedWarnings);
        } catch (e: any) {
            let errors = ["Unexpected error occurred."];
            if (e.response) {
                errors = new (Helpers as any)().getApiErrors(e.response.data);
            }
            let errorMsg = errors.join(" ");
            setError({
                open: true,
                errorHeader: "Error parsing file",
                errorMessage: errorMsg
            })
        }
    }
    const onDrop = async (files: File[]) => {
        if (!files || !files.length) return;
        setFile(files[0]);
        setLoading(true);
        await parseFile(files[0]);
        setLoading(false);
    }
    const loadImportProfiles = async () => {
        setLoading(true);
        let profiles: any = await (loadProfiles as any)(profileType);
        setAvailableProfiles(profiles);
        let storedName = storageHelper.get(storageProfileName);
        if (storedName) {
            let foundProfile = profiles.find((r: any) => r.Name === storedName);
            setProfile((prevState) => ({...prevState, ...foundProfile}));
        } else setProfile(blankProfile);
        setLoading(false);
        return profiles;
    }
    //endregion
    useEffect(() => {
        if(setTitle) {
            switch(type) {
                case "categories":
                    setTitle('POC: Import Categories', true);
                    break;
                case "attributes":
                    setTitle('POC: Import Attributes', true);
                    break;
                case "attributeSets":
                    setTitle('POC: Import Attribute Sets', true);
                    break;
                default:
                    setTitle('POC: Import', true);
            }
        }
    }, [type, setTitle]);
    useEffect(() => {
        const getData = async () => {
            await loadImportProfiles();
        }
        setProfile({...blankProfile});
        setFile(null);
        setValidationWarnings([]);
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentImportType]);
    useEffect(() => {
        if (setTitle) {
            if (type === "categories") {
                setTitle('POC Category Imports');
            } else if (type === "attributes") {
                setTitle('POC Attribute Imports');
            } else if (type === "attributeSets") {
                setTitle('POC Attribute Set Imports');
            } else {
                setTitle('POC: Import');
            }
        }
    }, [setTitle, type]);
    return (
        <Grid>
            <LinearProgress

                style={{
                    width: "calc(100% + 2em)",
                    height: '0.4em',
                    position: "relative",
                    top: "-1em",
                    right: "1em",
                    visibility: updating ? undefined : 'hidden'
                }}
                variant='indeterminate'/>
            <Grid container style={{gap: pageContentGap, padding: "24px 14px", flexDirection: "column"}}>
                <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{height: "36px", width: "36px"}}
                        onClick={(e) => {
                            e.preventDefault();
                            if (props.location.key === undefined)
                                props.history.push("/productonboardingcenter/catalog?catalog=retailer");
                            else
                                props.history.goBack();
                        }}
                        size="large">
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>
                    <Header>
                        Imports
                    </Header>
                </Grid>
                <Grid container style={{gap: pageContentGap}}>
                    <Paper>
                        <CatalogTab title={"Categories"} description={"Import categories"}
                                    selected={currentImportType === "categories"}
                                    onClick={() => {
                                        updateImportType("categories");
                                    }}
                                    style={{width: "300px"}}
                        />
                    </Paper>
                    <Paper>
                        <CatalogTab title={"Attributes"} description={"Import attributes"}
                                    selected={currentImportType === "attributes"}
                                    onClick={() => {
                                        updateImportType("attributes");
                                    }}
                                    style={{width: "300px"}}
                        />
                    </Paper>
                    <Paper>
                        <CatalogTab title={"Attribute Sets"} description={"Import attribute sets"}
                                    selected={currentImportType === "attributeSets"}
                                    onClick={() => {
                                        updateImportType("attributeSets");
                                    }}
                                    style={{width: "300px"}}
                        />
                    </Paper>
                </Grid>
                <Grid container style={{gap: pageContentGap}}>
                    <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                        <Grid item container
                              style={{flexDirection: "column", marginBottom: "16px", alignItems: "flex-start"}}>
                            <SectionTitle>
                                {type === "categories" ? "Categories File"
                                    : type === "attributes" ? "Attributes File"
                                        : "Attribute Sets File"}
                            </SectionTitle>
                        </Grid>
                        <Grid item size={12}>
                            <DropZone accept=".csv, .xlsx, .txt" style={{width: '100%', height: '10em'}}
                                      onDrop={onDrop}>
                                {({isDragAccept, isDragReject, acceptedFiles, rejectedFiles}: any) => {
                                    if (isDragAccept || isDragReject) {
                                        return "Drop here to upload this file.";
                                    }
                                    return file ? file.name : 'Drop a CSV/XLSX file here or click to choose a file.';
                                }}
                            </DropZone>
                        </Grid>
                    </Paper>
                    <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                        <EnhancedProfile
                            profile={profile} setProfile={setProfile as any}
                            profileType={profileType} availableProfiles={availableProfiles as any} blankProfile={blankProfile as any}
                            setError={setError as any} loadProfiles={loadImportProfiles as any}
                            loading={loading} stoageProfileName={storageProfileName} storageHelper={storageHelper}
                            operationType={"export"}
                        />
                    </Paper>
                    <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                        <Grid item container
                              style={{flexDirection: "column", marginBottom: "16px", alignItems: "flex-start"}}>
                            <SectionTitle>
                                Column Mapping
                            </SectionTitle>
                        </Grid>
                        <Grid container flex={1}>
                            {loading ? (
                                <Grid container justifyContent={"center"}>
                                    <CircularProgress/>
                                </Grid>
                            ) : (
                                <>
                                    {validationWarnings && validationWarnings.length > 0 ? (
                                            <Fragment>
                                                {validationWarnings.map((r, i) => (
                                                    <Notifier color="#FFB600" style={{marginBottom: '5px'}}
                                                              key={i}>{r}</Notifier>
                                                ))}
                                            </Fragment>
                                        )
                                        : ''
                                    }
                                    <EnhancedImportTable
                                        type={type} validationWarnings={validationWarnings} file={file}
                                        sourceOptions={parsedFields as any} targetOptions={[]}
                                        profile={profile} setProfile={setProfile}
                                    />
                                </>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item container size={12} justifyContent={"flex-end"}>
                    <Button variant="contained" color="primary"
                        disabled={updating}
                        onClick={(e) => {
                            e.preventDefault();
                            importItems();
                        }}
                    >
                        Import
                        {updating && <CircularProgress style={{marginLeft: "1em", color: "gray", width: "20px", height: "20px"}}/>}
                    </Button>
                </Grid>
            </Grid>
            {/*region Dialogs and Alerts*/}
            <EnhancedErrorDialog open={(error as ErrorState).open}
                                 errorHeader={(error as ErrorState).errorHeader}
                // Make sure error message is encoded so its readable
                                 errorMessage={(unescape as any)((error as ErrorState).errorMessage)}
                                 onClose={() => {
                                     setError({...(error as ErrorState), open: false});
                                     if (loading) props.history.goBack();
                                 }}/>
            <GenericDialog open={updated} setOpen={setUpdated}
                           title={"Import Complete"}
                           message={"The import has completed successfully.\n"}
            />
            {/*endregion*/}
        </Grid>
    );
}
